import React, { useState } from 'react';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const PersonalizationForm = ({ onDataChange, personalizationData }) => {
  const [formData, setFormData] = useState({
    firstName: '',
    familySize: '',
    challenges: '',
    launchTiming: '',
    audienceSegment: '',
    ...personalizationData
  });

  const familySizeOptions = [
    { value: '', label: 'Select family size' },
    { value: '2 (couple)', label: '2 (couple)' },
    { value: '3 (small family)', label: '3 (small family)' },
    { value: '4 (medium family)', label: '4 (medium family)' },
    { value: '5+ (large family)', label: '5+ (large family)' }
  ];

  const challengeOptions = [
    { value: '', label: 'Select biggest challenge' },
    { value: 'Morning routines', label: 'Morning routines' },
    { value: 'Homework time', label: 'Homework time' },
    { value: 'Chore management', label: 'Chore management' },
    { value: 'Screen time battles', label: 'Screen time battles' },
    { value: 'Bedtime routines', label: 'Bedtime routines' },
    { value: 'Family coordination', label: 'Family coordination' },
    { value: 'Task completion', label: 'Task completion' }
  ];

  const launchTimingOptions = [
    { value: '', label: 'When do you want access?' },
    { value: 'ASAP', label: 'As soon as possible' },
    { value: 'Early 2024', label: 'Early 2024' },
    { value: 'Mid 2024', label: 'Mid 2024' },
    { value: 'When ready', label: 'When it\'s fully ready' }
  ];

  const audienceSegmentOptions = [
    { value: '', label: 'Which describes you best?' },
    { value: 'millennial-parent', label: 'Millennial Parent (28-42)' },
    { value: 'genx-parent', label: 'Gen X Parent (43-58)' },
    { value: 'accessibility-focused', label: 'Accessibility-Focused Family' },
    { value: 'educator-therapist', label: 'Educator/Therapist' }
  ];

  const handleInputChange = (field, value) => {
    const updatedData = { ...formData, [field]: value };
    setFormData(updatedData);
    onDataChange(updatedData);
  };

  const resetForm = () => {
    const emptyData = {
      firstName: '',
      familySize: '',
      challenges: '',
      launchTiming: '',
      audienceSegment: ''
    };
    setFormData(emptyData);
    onDataChange(emptyData);
  };

  const fillSampleData = () => {
    const sampleData = {
      firstName: 'Sarah',
      familySize: '4 (medium family)',
      challenges: 'Morning routines',
      launchTiming: 'Early 2024',
      audienceSegment: 'millennial-parent'
    };
    setFormData(sampleData);
    onDataChange(sampleData);
  };

  return (
    <div className="bg-white rounded-xl shadow-soft border border-border p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-xl font-heading font-semibold text-foreground mb-2">
            Email Personalization
          </h3>
          <p className="text-text-secondary">
            Customize the email content with recipient-specific information
          </p>
        </div>
        <div className="flex items-center space-x-2">
          <Button
            variant="outline"
            size="sm"
            onClick={fillSampleData}
            iconName="Zap"
            iconPosition="left"
            iconSize={16}
          >
            Sample Data
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={resetForm}
            iconName="RotateCcw"
            iconPosition="left"
            iconSize={16}
          >
            Reset
          </Button>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Personal Information */}
        <div className="space-y-4">
          <h4 className="font-heading font-medium text-foreground flex items-center">
            <Icon name="User" size={18} className="mr-2 text-primary" />
            Personal Details
          </h4>
          
          <Input
            label="First Name"
            type="text"
            placeholder="Enter recipient's first name"
            value={formData?.firstName}
            onChange={(e) => handleInputChange('firstName', e?.target?.value)}
            description="Used in email greeting and personalization"
          />

          <Select
            label="Family Size"
            options={familySizeOptions}
            value={formData?.familySize}
            onChange={(value) => handleInputChange('familySize', value)}
            description="Helps tailor family-specific messaging"
          />
        </div>

        {/* Preferences & Challenges */}
        <div className="space-y-4">
          <h4 className="font-heading font-medium text-foreground flex items-center">
            <Icon name="Target" size={18} className="mr-2 text-secondary" />
            Preferences & Challenges
          </h4>
          
          <Select
            label="Biggest Challenge"
            options={challengeOptions}
            value={formData?.challenges}
            onChange={(value) => handleInputChange('challenges', value)}
            description="Addresses specific pain points in email"
          />

          <Select
            label="Launch Timing Preference"
            options={launchTimingOptions}
            value={formData?.launchTiming}
            onChange={(value) => handleInputChange('launchTiming', value)}
            description="Sets expectations for product availability"
          />
        </div>

        {/* Audience Segmentation */}
        <div className="md:col-span-2 space-y-4">
          <h4 className="font-heading font-medium text-foreground flex items-center">
            <Icon name="Users" size={18} className="mr-2 text-accent" />
            Audience Segmentation
          </h4>
          
          <Select
            label="Audience Segment"
            options={audienceSegmentOptions}
            value={formData?.audienceSegment}
            onChange={(value) => handleInputChange('audienceSegment', value)}
            description="Determines messaging tone and content focus"
          />
        </div>
      </div>
      {/* Personalization Preview */}
      {(formData?.firstName || formData?.familySize || formData?.challenges) && (
        <div className="mt-6 p-4 bg-surface rounded-lg border border-border">
          <h5 className="font-medium text-foreground mb-3 flex items-center">
            <Icon name="Eye" size={16} className="mr-2 text-gold" />
            Personalization Preview
          </h5>
          <div className="space-y-2 text-sm">
            {formData?.firstName && (
              <div className="flex items-center">
                <span className="text-text-secondary w-24">Greeting:</span>
                <span className="text-foreground">Welcome, {formData?.firstName}!</span>
              </div>
            )}
            {formData?.familySize && (
              <div className="flex items-center">
                <span className="text-text-secondary w-24">Family Size:</span>
                <span className="text-foreground">Family of {formData?.familySize}</span>
              </div>
            )}
            {formData?.challenges && (
              <div className="flex items-center">
                <span className="text-text-secondary w-24">Challenge:</span>
                <span className="text-foreground">Addresses "{formData?.challenges}"</span>
              </div>
            )}
            {formData?.launchTiming && (
              <div className="flex items-center">
                <span className="text-text-secondary w-24">Timeline:</span>
                <span className="text-foreground">Expected {formData?.launchTiming}</span>
              </div>
            )}
          </div>
        </div>
      )}
      {/* Tips */}
      <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
        <h5 className="font-medium text-blue-900 mb-2 flex items-center">
          <Icon name="Lightbulb" size={16} className="mr-2" />
          Personalization Tips
        </h5>
        <ul className="text-sm text-blue-800 space-y-1">
          <li>• Use first names to increase email open rates by 26%</li>
          <li>• Family size helps tailor specific organizational challenges</li>
          <li>• Challenge-specific content increases engagement by 40%</li>
          <li>• Audience segmentation improves conversion rates</li>
        </ul>
      </div>
    </div>
  );
};

export default PersonalizationForm;